var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useState, useMemo, useEffect } from 'react';
import { Segmented } from 'antd';
import { useTranslation } from 'react-i18next';
import validator from '@rjsf/validator-ajv8';
import { Form as RJSFForm } from '@rjsf/antd';
import PerFieldProvideField from './fields/PerFieldProvideField';
import OptionalCollapsibleField from './fields/OptionalCollapsibleField';
import ExpertEditorWidget from './ExpertEditorWidget';
import 'ace-builds/webpack-resolver';
import 'ace-builds/src-noconflict/mode-yaml';
import 'ace-builds/src-noconflict/theme-github';
import styles from './ParameterEditor.module.scss';
const ParameterEditor = ({ schema, formData, onChange, availableProviders }) => {
    const { t } = useTranslation();
    const [mode, setMode] = useState('Regular');
    const handleModeChange = (newMode) => {
        setMode(newMode);
    };
    // Helper function to resolve $ref references
    const resolveRef = (ref, root) => {
        const path = ref.split('/');
        if (path[0] !== '#')
            return null;
        let current = root;
        for (let i = 1; i < path.length; i++) {
            if (current === null || typeof current !== 'object' || !current.hasOwnProperty(path[i]))
                return null;
            current = current[path[i]];
        }
        return current;
    };
    // Helper function to collect defaults from schema including $refs
    const collectDefaults = (schema, rootSchema) => {
        const processSchema = (currentSchema, visited = new Set()) => {
            if (!currentSchema || typeof currentSchema !== 'object')
                return {};
            // Prevent infinite recursion
            const schemaKey = JSON.stringify(currentSchema);
            if (visited.has(schemaKey))
                return {};
            visited.add(schemaKey);
            let resolvedSchema = currentSchema;
            // Resolve $ref
            if (currentSchema.$ref) {
                const resolved = resolveRef(currentSchema.$ref, rootSchema);
                if (resolved) {
                    resolvedSchema = Object.assign(Object.assign({}, resolved), currentSchema);
                    delete resolvedSchema.$ref;
                }
            }
            const schemaDefaults = {};
            // Handle oneOf - use first schema with defaults (common pattern for union types)
            if (resolvedSchema.oneOf && Array.isArray(resolvedSchema.oneOf)) {
                for (const subSchema of resolvedSchema.oneOf) {
                    const subDefaults = processSchema(subSchema, new Set(visited));
                    if (Object.keys(subDefaults).length > 0) {
                        Object.assign(schemaDefaults, subDefaults);
                        break; // Use first schema with defaults
                    }
                }
            }
            // Handle allOf - merge all defaults
            if (resolvedSchema.allOf && Array.isArray(resolvedSchema.allOf)) {
                resolvedSchema.allOf.forEach((subSchema) => {
                    const subDefaults = processSchema(subSchema, new Set(visited));
                    Object.assign(schemaDefaults, subDefaults);
                });
            }
            // Handle properties
            if (resolvedSchema.properties) {
                Object.entries(resolvedSchema.properties).forEach(([key, prop]) => {
                    // Set direct defaults first
                    if (prop.default !== undefined) {
                        schemaDefaults[key] = prop.default;
                    }
                    // Set const values as defaults (common in message types)
                    else if (prop.const !== undefined) {
                        schemaDefaults[key] = prop.const;
                    }
                    // Recursively collect nested defaults
                    else {
                        const nestedDefaults = processSchema(prop, new Set(visited));
                        if (Object.keys(nestedDefaults).length > 0) {
                            schemaDefaults[key] = nestedDefaults;
                        }
                    }
                });
            }
            return schemaDefaults;
        };
        return processSchema(schema);
    };
    // Apply defaults on mount and when schema changes
    useEffect(() => {
        const defaults = collectDefaults(schema, schema);
        if (Object.keys(defaults).length > 0) {
            // Always merge defaults, but preserve existing user data
            const mergedData = Object.assign(Object.assign({}, defaults), formData);
            // Only trigger onChange if the merged data is different from current formData
            const hasChanges = JSON.stringify(mergedData) !== JSON.stringify(formData);
            if (hasChanges) {
                onChange(mergedData);
            }
        }
    }, [schema, JSON.stringify(formData)]); // Re-run when schema or formData changes
    const { fields, uiSchema } = useMemo(() => {
        const fields = {
            perFieldProvide: PerFieldProvideField,
            optionalCollapsible: OptionalCollapsibleField,
        };
        const resolveRefInSchema = (ref, root) => {
            const path = ref.split('/');
            if (path[0] !== '#')
                return null;
            let current = root;
            for (let i = 1; i < path.length; i++) {
                if (current === null || typeof current !== 'object' || !current.hasOwnProperty(path[i]))
                    return null;
                current = current[path[i]];
            }
            return current;
        };
        const hasRequiredChildren = (schema) => {
            if (!schema || typeof schema !== 'object')
                return false;
            // Check direct required fields
            if (schema.required && Array.isArray(schema.required) && schema.required.length > 0) {
                return true;
            }
            // Check properties for nested required fields
            if (schema.properties && typeof schema.properties === 'object') {
                return Object.values(schema.properties).some((propSchema) => hasRequiredChildren(propSchema));
            }
            // Check oneOf/allOf for required fields
            if (schema.oneOf && Array.isArray(schema.oneOf)) {
                return schema.oneOf.some((subSchema) => hasRequiredChildren(subSchema));
            }
            if (schema.allOf && Array.isArray(schema.allOf)) {
                return schema.allOf.some((subSchema) => hasRequiredChildren(subSchema));
            }
            return false;
        };
        // Use OptionalCollapsibleField for non-required fields to improve form navigation
        const shouldUseOptionalCollapsible = (property, key, parentSchema) => {
            var _a;
            const isRequired = (_a = parentSchema === null || parentSchema === void 0 ? void 0 : parentSchema.required) === null || _a === void 0 ? void 0 : _a.includes(key);
            // Don't use collapsible field for:
            // - Required fields
            // - Hidden fields (const values)
            if (isRequired || property.const !== undefined) {
                return false;
            }
            // Use collapsible field for optional fields that are:
            // - Objects with properties
            // - Arrays
            // - Fields with oneOf/allOf (complex types)
            // - String, number, integer, boolean fields
            // - Enum fields (to support provide functionality)
            // - Untyped fields
            return ((property.type === 'object' && property.properties) ||
                property.type === 'array' ||
                property.oneOf ||
                property.allOf ||
                property.type === 'string' ||
                property.type === 'number' ||
                property.type === 'integer' ||
                property.type === 'boolean' ||
                property.enum ||
                !property.type // untyped fields
            );
        };
        const buildUiSchema = (subSchema, root, providers, parentSchema) => {
            let currentSchema = Object.assign({}, subSchema);
            if (currentSchema.$ref) {
                const resolved = resolveRefInSchema(currentSchema.$ref, root);
                if (resolved) {
                    const { $ref } = currentSchema, rest = __rest(currentSchema, ["$ref"]);
                    currentSchema = Object.assign(Object.assign({}, resolved), rest);
                }
            }
            if (currentSchema.allOf) {
                let mergedProperties = {};
                currentSchema.allOf.forEach(part => {
                    const resolvedPart = part.$ref ? resolveRefInSchema(part.$ref, root) : part;
                    if (resolvedPart === null || resolvedPart === void 0 ? void 0 : resolvedPart.properties) {
                        mergedProperties = Object.assign(Object.assign({}, mergedProperties), resolvedPart.properties);
                    }
                });
                currentSchema.properties = Object.assign(Object.assign({}, mergedProperties), currentSchema.properties);
            }
            const uiSchema = {};
            if (currentSchema.properties) {
                Object.keys(currentSchema.properties).forEach(key => {
                    const property = currentSchema.properties[key];
                    if (shouldUseOptionalCollapsible(property, key, currentSchema)) {
                        uiSchema[key] = Object.assign({ 'ui:field': 'optionalCollapsible', 'ui:options': { availableProviders: providers }, parentSchema: currentSchema, rootSchema: root }, buildUiSchema(property, root, providers, currentSchema));
                    }
                    else if ((property.type === 'object' && property.properties) || property.oneOf || property.allOf) {
                        uiSchema[key] = buildUiSchema(property, root, providers, currentSchema);
                    }
                    else if (property.const !== undefined) {
                        uiSchema[key] = {
                            'ui:widget': 'hidden',
                        };
                    }
                    else if (property.type === 'boolean') {
                        uiSchema[key] = {
                            'ui:field': 'perFieldProvide',
                            'ui:options': {
                                availableProviders: providers,
                                checkedChildren: t('switchTrue', 'True'),
                                unCheckedChildren: t('switchFalse', 'False'),
                            },
                        };
                    }
                    else {
                        uiSchema[key] = {
                            'ui:field': 'perFieldProvide',
                            'ui:options': { availableProviders: providers },
                        };
                    }
                });
            }
            if (currentSchema.oneOf) {
                uiSchema.oneOf = currentSchema.oneOf.map(item => buildUiSchema(item, root, providers, parentSchema));
            }
            return uiSchema;
        };
        const uiSchema = buildUiSchema(schema, schema, availableProviders);
        return { fields, uiSchema };
    }, [schema, availableProviders, t]);
    return (React.createElement("div", null,
        React.createElement("div", { className: styles.editorSwitch },
            React.createElement(Segmented, { options: [
                    { label: t('editorModeRegular', 'Regular'), value: 'Regular' },
                    { label: t('editorModeExpert', 'Expert'), value: 'Expert' },
                ], value: mode, onChange: (value) => handleModeChange(value) })),
        mode === 'Expert' ? (React.createElement(ExpertEditorWidget, { schema: schema, value: formData, onChange: onChange, disabled: false, readonly: false })) : (React.createElement(RJSFForm, { schema: schema, formData: formData, onChange: (e) => {
                // Apply defaults to new data if needed, but avoid infinite loops
                const defaults = collectDefaults(schema, schema);
                const mergedData = Object.assign(Object.assign({}, defaults), e.formData);
                // Only call onChange if there's a real change
                if (JSON.stringify(mergedData) !== JSON.stringify(formData)) {
                    onChange(mergedData);
                }
            }, validator: validator, fields: fields, uiSchema: uiSchema },
            React.createElement("div", null)))));
};
export default ParameterEditor;
